import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import clsx from "clsx";
import { Menu } from "lucide-react";
import { IPagesLink } from "./Footer";

interface Props {
  toggleContact: () => void;
}

const Navbar: React.FC<Props> = ({ toggleContact }) => {
  const { pathname } = useLocation();
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [toggleMenu, setToggleMenu] = useState<boolean>(false);
  const [showSublinks, setShowSublinks] = useState<boolean>(false);
  const [showWings, setShowWings] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (window.scrollY > 0) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  }, []);

  useEffect(() => {
    // check the initial scroll position
    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSublinks(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const navigationLinks = [
    // {
    //   html: <span>Our Wings</span>,
    //   url: "/our-wings",
    // },
    {
      html: <span>Company</span>,
      url: "/company",
    },
    {
      html: <span>About Us</span>,
      url: "/aboutus", // Note: This link is duplicated; consider if it should point to a different URL.
    },
    {
      html: (
        <>
          <span>Careers</span>
          <span
            className="px-1 ml-1 rounded text-neutral-700 font-medium hiring-badge"
            style={{
              animation: "bgChange 2s infinite alternate",
            }}
          >
            We are Hiring!
          </span>
        </>
      ),
      url: "/careers",
    },
    {
      html: <span onClick={toggleContact}>Contact</span>,
      url: "#",
    },
  ];

  const subPageLinks: IPagesLink[] = [
    {
      url: "/consultancy",
      name: "Manpower Recruitment and Staffing Consultancy",
    },
    {
      url: "/training",
      name: "Corporate Training Solutions",
    },
    {
      url: "/learning",
      name: "Learning Management System",
    },
    {
      url: "/skill-development",
      name: "Skill Development",
    },
    {
      url: "/tourism",
      name: "Tourism Technology Solutions",
    },
  ];

  const handleToggleMenu = () => setToggleMenu((prev) => !prev);

  return (
    <nav
      className={clsx(
        "w-full p-5 max-w-[1900px] fixed top-0 z-[100] h-[4.5rem] items-center justify-center transition-colors duration-200",
        {
          "bg-transparent": pathname === "/" && !isScrolled,
          "bg-[#34368A]":
            (pathname !== "/" && pathname !== "/aboutus") || isScrolled,
        },
      )}
    >
      <div className="flex items-center text-white justify-between text-xs sm:text-sm w-full gap-10 lg:px-10 xl:px-[4.1rem]">
        <Link to="/" className="w-44 lg:w-56">
          <img src="/logo.png" alt="" />
        </Link>
        <div className="hidden lg:flex-1 lg:flex justify-end gap-4 ml-auto">
          <div className="gap-10 text-xs items-center lg:flex hidden">
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={() => setShowSublinks(true)}
              onMouseLeave={() => setShowSublinks(false)}
            >
              <div
                className="cursor-pointer px-4 py-2 transition-all ease-out duration-200 text-white hover:opacity-80"
                onClick={() => setShowSublinks(!showSublinks)}
              >
                <p>Our Wings</p>
              </div>

              {showSublinks && (
                <div className="absolute top-full -left-24 bg-white shadow-lg rounded-md mt-1 w-[20rem] py-2 z-50">
                  {subPageLinks.map((link, index) => (
                    <Link
                      key={index}
                      to={link.url}
                      onClick={() => setShowSublinks(false)}
                      className="block transition-all hover:opacity-80 ease-out px-4 py-2 text-neutral-700 hover:bg-neutral-100 duration-200"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {/* <DropdownMenu>
              <DropdownMenuTrigger>Our Wings</DropdownMenuTrigger>
              <DropdownMenuContent className="z-[120] lg:flex lg:flex-col hidden mt-2 w-[20rem]">
                {subPageLinks.map((link, index) => {
                  return (
                    <Link
                      to={link.url}
                      className={clsx(
                        "text-xs cursor-pointer block px-2 py-2 rounded hover:text-neutral-500 transition-all duration-300 ease-out",
                        {
                          "bg-neutral-200 hover:text-black":
                            pathname === link.url,
                        },
                      )}
                    >
                      {link.name}
                    </Link>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu> */}
            <div className="flex gap-10 text-xs">
              {navigationLinks.map((link) => {
                return (
                  <Link
                    to={link.url}
                    className={clsx(
                      " transition-all hover:opacity-80 duration-200 ease-out",
                      {
                        "underline-offset-4 underline":
                          pathname === link.url ||
                          pathname.split("/")[0] === link.url,
                      },
                    )}
                    key={link.url}
                  >
                    {link.html}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
        <div className="lg:hidden flex justify-end">
          <Menu
            size={24}
            className="cursor-pointer"
            onClick={handleToggleMenu}
          />
        </div>
        {toggleMenu && (
          <div className="fixed top-[4rem] left-0 px-4 mx-auto w-full h-auto lg:hidden z-[100] bg-[#34368A]">
            <div className="lg:hidden block animate-menu-animation transform-gpu transition-transform duration-200ms ease-in-out">
              <div
                className="block py-4 cursor-pointer text-white text-left p-3 transition-all duration-200 ease-out"
                onClick={() => setShowWings((prev) => !prev)}
              >
                Wings
              </div>
              {showWings && (
                <div className="pl-5">
                  {subPageLinks.map((link, index) => {
                    return (
                      <Link
                        to={link.url}
                        onClick={handleToggleMenu}
                        className={clsx(
                          "text-xs cursor-pointer block px-2 py-2 rounded hover:text-neutral-300 transition-all duration-300 ease-out",
                          {
                            "underline hover:text-black": pathname === link.url,
                          },
                        )}
                      >
                        {link.name}
                      </Link>
                    );
                  })}
                </div>
              )}
              {navigationLinks.map((link) => {
                return (
                  <>
                    {link.url !== "#" ? (
                      <Link
                        to={link.url}
                        onClick={handleToggleMenu}
                        className={clsx(
                          "block py-4 text-white text-left p-3 transition-all duration-200 ease-out",
                          {
                            "underline underline-offset-4":
                              pathname === link.url,
                          },
                        )}
                        key={link.url}
                      >
                        {link.html}
                      </Link>
                    ) : (
                      <div
                        onClick={handleToggleMenu}
                        className={clsx(
                          "block py-4 text-white text-left p-3 transition-all duration-200 ease-out",
                        )}
                        key={link.url}
                      >
                        {link.html}
                      </div>
                    )}
                  </>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
